import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/Button/Button";
import { Card } from "../components/Card/Card";
import { Logo } from "../components/Logo/Logo";
import "./Signup.css";
import { config } from "../config";

const STATUS_TOKEN_KEY = "signup_status_token";
const COOLDOWN_END_KEY = "resend_cooldown_end";

interface SignupFormState {
  username: string;
  displayname: string;
  email: string;
}

type SignupErrors = Partial<Record<keyof SignupFormState, string>>;

type TokenStatus = "pending" | "available" | "error";

interface TokenResponse {
  status: TokenStatus;
  token_link?: string;
  message?: string;
}

interface RegistrationStatusResponse {
  status: TokenStatus;
  message?: string;
}

interface AccountCreationResponse {
  ok: boolean;
  status_token: string;
}

const initial_form_state: SignupFormState = {
  username: "",
  displayname: "",
  email: "",
};

const username_pattern =
  /^[a-z][a-z0-9-]*$/;

const is_valid_username = (value: string): boolean =>
  username_pattern.test(value) && value.length >= 3 && value.length <= 32;

const poll_delay = (duration = 2000): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, duration));

const extract_error = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return "Something went wrong. Please try again.";
};

const validate_field = (
  field: keyof SignupFormState,
  value: string,
): SignupErrors => {
  const email_pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const next_errors: SignupErrors = {};

  if (field === "username") {
    if (!value.trim()) {
      next_errors.username = "Username is required.";
    } else if (!is_valid_username(value)) {
      next_errors.username =
        "Username must start with a letter and contain only lowercase letters, digits, and hyphens (3-32 characters).";
    }
  }

  if (field === "displayname") {
    if (!value.trim()) {
      next_errors.displayname = "Display name is required.";
    } else if (value.length > 128) {
      next_errors.displayname =
        "Display name must be 128 characters or less.";
    }
  }

  if (field === "email") {
    if (!value.trim()) {
      next_errors.email = "Email is required.";
    } else if (!email_pattern.test(value)) {
      next_errors.email = "Enter a valid email.";
    }
  }

  return next_errors;
};

const validate_form = (payload: SignupFormState): SignupErrors => {
  const next_errors: SignupErrors = {};
  const email_pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!payload.username.trim()) {
    next_errors.username = "Username is required.";
  } else if (!is_valid_username(payload.username)) {
    next_errors.username =
      "Username must start with a letter and contain only lowercase letters, digits, and hyphens (3-32 characters).";
  }

  if (!payload.displayname.trim()) {
    next_errors.displayname = "Display name is required.";
  } else if (payload.displayname.length > 128) {
    next_errors.displayname =
      "Display name must be 128 characters or less.";
  }

  if (!payload.email.trim()) {
    next_errors.email = "Email is required.";
  } else if (!email_pattern.test(payload.email)) {
    next_errors.email = "Enter a valid email.";
  }

  return next_errors;
};

export const Signup: React.FC = () => {
  const [form_state, set_form_state] = useState<SignupFormState>(
    initial_form_state,
  );
  const [errors, set_errors] = useState<SignupErrors>({});
  const [status, set_status] = useState<
    "idle" | "creating" | "polling" | "success" | "error" | "timeout"
  >("idle");
  const [token_response, set_token_response] =
    useState<TokenResponse | null>(null);
  const [error_message, set_error_message] = useState<string>("");
  const [is_email_registered_error, set_is_email_registered_error] = useState<boolean>(false);
  const [polling_attempt, set_polling_attempt] = useState<number>(0);
  const [can_resend, set_can_resend] = useState<boolean>(false);
  const [resending, set_resending] = useState<boolean>(false);
  const [resend_cooldown, set_resend_cooldown] = useState<number>(0);
  const [status_token, set_status_token] = useState<string>(() => {
    try {
      const cooldown_end = sessionStorage.getItem(COOLDOWN_END_KEY);
      if (cooldown_end) {
        const end_time = parseInt(cooldown_end, 10);
        const remaining = Math.max(0, Math.floor((end_time - Date.now()) / 1000));
        if (remaining > 0) {
          set_resend_cooldown(remaining);
          set_can_resend(false);
        } else {
          sessionStorage.removeItem(COOLDOWN_END_KEY);
        }
      }

      return sessionStorage.getItem(STATUS_TOKEN_KEY) || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    if (status_token) sessionStorage.setItem(STATUS_TOKEN_KEY, status_token);
  }, [status_token]);

  useEffect(() => {
    if (status_token && status === "idle") {
      set_status("polling");
      set_can_resend(true);
      poll_for_token(status_token);
    }
  }, [status, status_token]);

  const handle_field_change = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const { name, value } = event.target;
    set_form_state((current_state) => ({ ...current_state, [name]: value }));

    if (errors[name as keyof SignupFormState]) {
      const field_errors = validate_field(name as keyof SignupFormState, value);
      set_errors((current_state) => ({ ...current_state, ...field_errors }));
    }
  };

  const handle_field_blur = (
    event: React.FocusEvent<HTMLInputElement>,
  ): void => {
    const { name, value } = event.target;
    const field_errors = validate_field(name as keyof SignupFormState, value);

    if (Object.keys(field_errors).length > 0) {
      set_errors((current_state) => ({ ...current_state, ...field_errors }));
      return;
    }

    set_errors((current_state) => {
      const next_errors = { ...current_state };
      delete next_errors[name as keyof SignupFormState];
      return next_errors;
    });
  };

  const poll_for_token = async (status_token: string): Promise<void> => {
    const max_attempts = 10;
    set_polling_attempt(0);
    set_can_resend(true);

    for (let attempt = 0; attempt < max_attempts; attempt += 1) {
      set_polling_attempt(attempt + 1);

      try {
        const response = await fetch(
          `${config.registration.baseUrl}/api/registration-status/${status_token}`,
          { credentials: "omit" }
        );
        const data = (await response.json()) as RegistrationStatusResponse;

        if (data.status === "available") {
          set_token_response({ status: "available", message: data.message });
          set_status("success");
          sessionStorage.removeItem(STATUS_TOKEN_KEY);
          sessionStorage.removeItem(COOLDOWN_END_KEY);
          set_resend_cooldown(0);
          set_can_resend(true);
          return;
        }

        if (data.status === "error") {
          set_error_message(data.message || "Failed to retrieve token.");
          set_status("error");
          sessionStorage.removeItem(STATUS_TOKEN_KEY);
          sessionStorage.removeItem(COOLDOWN_END_KEY);
          set_resend_cooldown(0);
          set_can_resend(true);
          return;
        }
      } catch (error) {
        console.error("Token polling error:", error);
      }

      await poll_delay();
    }

    set_error_message(
      "Verification email not confirmed within expected time. Please check your email and click the verification link, or contact support if you need assistance."
    );
    sessionStorage.removeItem(STATUS_TOKEN_KEY);
    sessionStorage.removeItem(COOLDOWN_END_KEY);
    set_resend_cooldown(0);
    set_can_resend(true);
    set_status("timeout");
  };

  const handle_resend = async (): Promise<void> => {
    if (!status_token || resending || resend_cooldown > 0) {
      return;
    }

    set_resending(true);
    set_error_message("");

    try {
      const response = await fetch(
        `${config.registration.baseUrl}/api/accounts/resend-status`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "omit",
          body: JSON.stringify({ status_token }),
        }
      );

      if (!response.ok) {
        let error_data: { error?: string; status_token?: string } = {};
        try {
          error_data = (await response.json()) as { error?: string; status_token?: string };
        } catch {
          error_data = {};
        }

        if (error_data.status_token) {
          set_status("polling");
          set_error_message("We already have a pending registration for this email. Please check your email to complete verification.");
          set_status_token(error_data.status_token);
          set_resend_cooldown(60);
          sessionStorage.setItem(COOLDOWN_END_KEY, (Date.now() + 60000).toString());
          await poll_for_token(error_data.status_token);
          return;
        }
        throw new Error(error_data.error || "Failed to resend verification email.");
      }

      set_resend_cooldown(60);
      sessionStorage.setItem(COOLDOWN_END_KEY, (Date.now() + 60000).toString());
      set_can_resend(false);
      set_error_message("");
    } catch (error) {
      set_error_message(extract_error(error));
    } finally {
      set_resending(false);
    }
  };

  useEffect(() => {
    if (resend_cooldown > 0) {
      const timer = setTimeout(() => {
        set_resend_cooldown((prev) => {
          const next = prev - 1;
          if (next === 0) {
            set_can_resend(true);
            sessionStorage.removeItem(COOLDOWN_END_KEY);
          }
          return next;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [resend_cooldown]);

  const handle_form_submit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    const next_errors = validate_form(form_state);

    if (Object.keys(next_errors).length > 0) {
      set_errors(next_errors);
      set_status("idle");
      return;
    }

    set_errors({});
    set_status("creating");
    set_error_message("");
    set_is_email_registered_error(false);

    try {
      const response = await fetch(
        `${config.registration.baseUrl}/api/accounts`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "omit",
          body: JSON.stringify(form_state),
        }
      );

      if (!response.ok) {
        let error_data: { error?: string } = {};
        try {
          error_data = (await response.json()) as { error?: string };
        } catch {
          error_data = {};
        }
        const message = error_data.error || "Account creation failed.";
        if (message.toLowerCase().includes("pending verification")) {
          set_status("polling");
          set_resend_cooldown(60);
          sessionStorage.setItem(COOLDOWN_END_KEY, (Date.now() + 60000).toString());
        }
        if (
          message.toLowerCase().includes("email already registered") ||
          message.toLowerCase().includes("already exists")
        ) {
          set_is_email_registered_error(true);
        }
        throw new Error(message);
      }

      const creation_data = (await response.json()) as AccountCreationResponse;
      if (!creation_data?.status_token) {
        throw new Error("Missing registration status token.");
      }
      set_status("polling");
      set_status_token(creation_data.status_token);
      set_resend_cooldown(60);
      sessionStorage.setItem(COOLDOWN_END_KEY, (Date.now() + 60000).toString());
      await poll_for_token(creation_data.status_token);
    } catch (error) {
      set_status("error");
      set_error_message(extract_error(error));
    }
  };

  if (status === "timeout") {
    return (
      <div className="signup-page">
        <div className="signup-splash">
          <Logo variant="dark" size="medium" />
          <h2>Verification Timeout</h2>
          <p>{error_message}</p>
          <p className="signup-spam-note">
            <strong>Note:</strong> Check your spam folder if you haven't received the verification email.
          </p>
          {can_resend && (
            <div className="signup-button-group">
              <button
                onClick={handle_resend}
                className="signup-resend-button"
                disabled={resending || resend_cooldown > 0}
              >
                {resending
                  ? "Sending..."
                  : resend_cooldown > 0
                    ? `Resend in ${resend_cooldown}s`
                    : "Resend Verification Email"}
              </button>
              <button onClick={() => window.location.reload()} className="signup-token-link signup-token-link--button">
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="signup-page">
        <div className="signup-splash">
          <Logo variant="dark" size="medium" />
          <h2>Account Created Successfully!</h2>
          <p>{token_response?.message || "Your account has been created."}</p>
          {token_response?.token_link ? (
            <a
              href={token_response.token_link}
              className="signup-token-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Activate Account
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-header">
          <Logo variant="dark" size="medium" />
          <h2>Create Your Account</h2>
          <p>Join FancyCloud and start managing your infrastructure</p>
        </div>

        <Card className="signup-card" variant="bordered" padding="large">
          <form className="signup-form" onSubmit={handle_form_submit}>
            <div
              className={`signup-field ${
                errors.username ? "signup-field--error" : ""
              }`}
            >
              <label htmlFor="username">Username</label>
              <input
                id="username"
                name="username"
                type="text"
                required
                placeholder="username"
                value={form_state.username}
                onChange={handle_field_change}
                onBlur={handle_field_blur}
                className={errors.username ? "signup-input--error" : undefined}
                aria-invalid={Boolean(errors.username)}
                disabled={status === "creating" || status === "polling"}
              />
              <span className="signup-helper">
                Start with a letter; lowercase letters, digits, and hyphens only
              </span>
              {errors.username && (
                <span className="signup-error">{errors.username}</span>
              )}
            </div>

            <div
              className={`signup-field ${
                errors.displayname ? "signup-field--error" : ""
              }`}
            >
              <label htmlFor="displayname">Display Name</label>
              <input
                id="displayname"
                name="displayname"
                type="text"
                required
                placeholder="display.name"
                value={form_state.displayname}
                onChange={handle_field_change}
                onBlur={handle_field_blur}
                className={
                  errors.displayname ? "signup-input--error" : undefined
                }
                aria-invalid={Boolean(errors.displayname)}
                disabled={status === "creating" || status === "polling"}
              />
              <span className="signup-helper">
                Your full name or preferred display name
              </span>
              {errors.displayname && (
                <span className="signup-error">{errors.displayname}</span>
              )}
            </div>

            <div
              className={`signup-field ${
                errors.email ? "signup-field--error" : ""
              }`}
            >
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                inputMode="email"
                placeholder="you@example.com"
                value={form_state.email}
                onChange={handle_field_change}
                onBlur={handle_field_blur}
                className={errors.email ? "signup-input--error" : undefined}
                aria-invalid={Boolean(errors.email)}
                disabled={status === "creating" || status === "polling"}
              />
              {errors.email && (
                <span className="signup-error">{errors.email}</span>
              )}
            </div>

            <Button
              size="large"
              variant="primary"
              type="submit"
              fullWidth
              loading={status === "creating" || status === "polling"}
              disabled={status === "creating" || status === "polling"}
            >
              {status === "creating"
                ? "Creating Account..."
                : status === "polling" && polling_attempt > 0
                  ? `Waiting for email verification (attempt ${polling_attempt}/10)...`
                  : status === "polling"
                  ? "Waiting for email verification..."
                  : "Create Account"}
            </Button>

            {status === "polling" && (
              <div className="signup-polling-info">
                <p className="signup-spam-note">
                  <strong>Note:</strong> Check your spam folder if you haven't received the verification email.
                </p>
                {can_resend && (
                  <button
                    type="button"
                    onClick={handle_resend}
                    className="signup-resend-button"
                    disabled={resending || resend_cooldown > 0}
                  >
                    {resending
                      ? "Sending..."
                      : resend_cooldown > 0
                        ? `Resend in ${resend_cooldown}s`
                        : "Resend Verification Email"}
                  </button>
                )}
              </div>
            )}

            {error_message && (
              <div className="signup-status signup-status--error">
                <p>{error_message}</p>
                {is_email_registered_error && (
                  <p className="signup-reset-password-link">
                    Already have an account?{" "}
                    <Link to="/reset-password">Reset your password</Link>
                  </p>
                )}
              </div>
            )}
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Signup;
